/**
 * My Work — personal inbox of tasks assigned to the current user.
 *
 * FRIDAY-76: the tasks are grouped under their workspace and their project,
 * with a separator for each group, and the view can filter and sort them.
 * Clicking a row opens the task in its board.
 */

import { useNavigate } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import {
  MY_WORK_SORT_OPTIONS,
  MY_WORK_STATUS_OPTIONS,
  groupMyTasks,
  matchesQuery,
  matchesStatus,
  sortMyTasks,
  useMyTasks,
  type MyWorkSortKey,
  type MyWorkStatus,
  type MyWorkTask,
} from '@/features/projects/my-work'
import { cn } from '@/lib/utils'
import { TaskDrawer } from '@/components/task/Drawer'
import { SortSelect } from '@/components/ui/SortSelect'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Project, Task } from '@friday/shared/realtime'

export function MyWorkPage() {
  const [includeDone, setIncludeDone] = useState(false)
  const [openTask, setOpenTask] = useState<MyWorkTask | null>(null)
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<MyWorkStatus>('all')
  const [workspaceId, setWorkspaceId] = useState('all')
  const [projectId, setProjectId] = useState('all')
  const [sortKey, setSortKey] = useState<MyWorkSortKey>('due')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const navigate = useNavigate()
  const my = useMyTasks(includeDone)
  const tasks = my.data ?? []

  // The groups the filters can narrow down: one entry per workspace and per
  // project the current user has a task in.
  const { workspaces, projects } = useMemo(() => {
    const workspaceMap = new Map<string, { id: string; name: string }>()
    const projectMap = new Map<string, { id: string; name: string; key: string; workspaceId: string }>()
    for (const task of tasks) {
      if (task.workspace) workspaceMap.set(task.workspace.id, { id: task.workspace.id, name: task.workspace.name })
      if (task.project) {
        projectMap.set(task.project.id, {
          id: task.project.id,
          name: task.project.name,
          key: task.project.key,
          workspaceId: task.project.workspaceId,
        })
      }
    }
    return {
      workspaces: [...workspaceMap.values()].sort((a, b) => a.name.localeCompare(b.name)),
      projects: [...projectMap.values()].sort((a, b) => a.name.localeCompare(b.name)),
    }
  }, [tasks])

  const visible = useMemo(() => {
    const now = Date.now()
    const filtered = tasks.filter(
      (t) =>
        matchesStatus(t, status, now) &&
        matchesQuery(t, query) &&
        (workspaceId === 'all' || t.workspace?.id === workspaceId) &&
        (projectId === 'all' || t.project?.id === projectId),
    )
    return sortMyTasks(filtered, sortKey, sortDir)
  }, [tasks, status, query, workspaceId, projectId, sortKey, sortDir])

  const groups = useMemo(() => groupMyTasks(visible), [visible])

  // Resolve project + workspace for the open task (so the drawer can mount).
  const projectQ = useQuery({
    queryKey: ['project-for-task', openTask?.projectId],
    queryFn: () => api<Project>(`/api/v1/projects/${openTask!.projectId}`),
    enabled: !!openTask && !openTask.project,
  })

  function open(t: MyWorkTask) {
    // A task that knows its project opens in that board, with the drawer.
    if (t.workspace && t.project) {
      void navigate({
        to: '/w/$wsId/p/$pKey/t/$tKey',
        params: { wsId: t.workspace.id, pKey: t.project.key, tKey: t.key },
      })
      return
    }
    setOpenTask(t)
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-2 border-b border-surface-200 bg-white/60 px-4 py-2 backdrop-blur dark:border-surface-700 dark:bg-surface-900/60 md:pr-14">
        <h1 className="text-sm font-semibold">My Work</h1>
        <span className="text-[10px] text-surface-500">
          {visible.length} of {tasks.length} task{tasks.length === 1 ? '' : 's'}
        </span>
        <label className="ml-auto flex items-center gap-1.5 text-xs text-surface-600 dark:text-surface-300">
          <input
            type="checkbox"
            checked={includeDone}
            onChange={(e) => setIncludeDone(e.target.checked)}
          />
          Include done
        </label>
      </header>

      <div className="border-surface-200 dark:border-surface-700 flex flex-wrap items-center gap-2 border-b px-4 py-2 text-xs">
        <input
          type="search"
          aria-label="Search my tasks"
          placeholder="Search my tasks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="input w-48 py-1 text-xs"
        />
        <select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value as MyWorkStatus)}
          className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1"
        >
          {MY_WORK_STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        {workspaces.length > 1 && (
          <select
            aria-label="Filter by workspace"
            value={workspaceId}
            onChange={(e) => {
              setWorkspaceId(e.target.value)
              setProjectId('all')
            }}
            className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1"
          >
            <option value="all">All workspaces</option>
            {workspaces.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        )}
        {projects.length > 1 && (
          <select
            aria-label="Filter by project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="border-surface-300 dark:border-surface-700 rounded border bg-transparent px-2 py-1"
          >
            <option value="all">All projects</option>
            {projects
              .filter((p) => workspaceId === 'all' || p.workspaceId === workspaceId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        )}
        <SortSelect
          value={sortKey}
          options={MY_WORK_SORT_OPTIONS}
          direction={sortDir}
          onChange={(value) => {
            setSortKey(value as MyWorkSortKey)
            setSortDir('asc')
          }}
          onDirectionChange={setSortDir}
        />
      </div>

      <div className="flex-1 overflow-auto p-4">
        {my.isLoading ? (
          <div className="text-sm text-surface-500">Loading…</div>
        ) : tasks.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center dark:border-surface-700">
            <div className="text-2xl">🌴</div>
            <p className="mt-2 text-sm text-surface-500">Nothing on your plate. Take a break.</p>
          </div>
        ) : visible.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-200 p-12 text-center dark:border-surface-700">
            <p className="text-sm text-surface-500">No task matches these filters.</p>
          </div>
        ) : (
          groups.map((group) => (
            <section key={group.workspaceId} className="mb-6" aria-label={group.workspace?.name ?? 'Workspace'}>
              <h2
                data-testid="my-work-workspace"
                className="text-surface-500 border-surface-200 dark:border-surface-700 flex items-center gap-2 border-b pb-1 text-xs font-semibold uppercase tracking-wider"
              >
                {group.workspace?.name ?? 'Workspace'}
                <span className="text-surface-400 font-normal normal-case">
                  {group.projects.reduce((sum, p) => sum + p.tasks.length, 0)} tasks
                </span>
              </h2>
              {group.projects.map((project) => (
                <div key={project.projectId} className="mt-3">
                  <h3
                    data-testid="my-work-project"
                    className="flex items-center gap-2 text-sm font-medium"
                  >
                    {project.project && (
                      <span
                        aria-hidden="true"
                        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: project.project.color }}
                      />
                    )}
                    {project.project?.name ?? 'Project'}
                    {project.project && (
                      <span className="text-surface-500 font-mono text-[10px]">
                        {project.project.key}
                      </span>
                    )}
                    <span className="text-surface-400 text-[10px] font-normal">
                      {project.tasks.length} task{project.tasks.length === 1 ? '' : 's'}
                    </span>
                  </h3>
                  <ul className="mt-1.5 space-y-1.5">
                    {project.tasks.map((t) => (
                      <li key={t.id}>
                        <button
                          onClick={() => open(t)}
                          className="flex w-full items-center gap-3 rounded-md border border-surface-200 bg-white p-2.5 text-left text-sm hover:border-brand-500 dark:border-surface-700 dark:bg-surface-800"
                        >
                          <PriorityDot p={t.priority} />
                          <span className="font-mono text-[10px] text-surface-500">{t.key}</span>
                          <span data-testid="my-work-task-title" className="flex-1 truncate">
                            {t.title}
                          </span>
                          {t.dueDate && (
                            <span
                              className={cn(
                                'text-[11px]',
                                new Date(t.dueDate) < new Date() ? 'text-red-500' : 'text-surface-500',
                              )}
                            >
                              {new Date(t.dueDate).toLocaleDateString(undefined, {
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          )}
                          {t.storyPoints != null && (
                            <span className="rounded bg-surface-100 px-1.5 py-0.5 font-mono text-[10px] dark:bg-surface-700">
                              {t.storyPoints}
                            </span>
                          )}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          ))
        )}
      </div>
      {openTask && projectQ.data && (
        <TaskDrawer
          wsId={projectQ.data.workspaceId}
          pId={projectQ.data.id}
          taskId={openTask.id}
          onClose={() => setOpenTask(null)}
        />
      )}
    </div>
  )
}

function PriorityDot({ p }: { p: Task['priority'] }) {
  return (
    <span
      className={cn(
        'h-2 w-2 rounded-full',
        p === 'urgent' && 'bg-red-500',
        p === 'high' && 'bg-amber-500',
        p === 'medium' && 'bg-blue-500',
        p === 'low' && 'bg-surface-400',
      )}
    />
  )
}
